import React from 'react';
import {
    RawPurchaseEntryBase,
    applyRawPurchaseEntryReferenceHeaderLocks,
    buildUpdatedRawPurchaseEntryFields,
    buildRawPurchaseEntryDetailPanelTypes,
    buildRawPurchaseEntryHiddenPanelTypes,
    buildRawPurchaseEntryTotalPanelDefinitions,
    RAW_PURCHASE_ENTRY_FIELD_IDS,
    buildRawPurchaseEntryReferenceHooks
} from './RawPurchaseEntryBase';
import { DeepCopy, DicVal, isEmpty } from '../modules/appLibrary';

const HEADER_LOCK_COLUMN_IDS = [
    "ZSR_ZAIRYOKBN",
    "ZSR_SIIREKIND",
    "ZSR_DENSYUBETU",
    "ZSR_DENKBN",
    "ZSR_SHIREBI",
    "ZSR_BRDCD",
    "ZSR_SOKOKBN",
    "ZSR_SOKOCD",
    "ZSR_SOKOSUBCD",
    "ZSR_INVOICENO",
    "ZSR_INVOICEEDA",
    "ZSR_SIIREKBN",
    "ZSR_KAIGAIKBN",
    "ZSR_TSUKATANI",
    "ZSR_TEKIYO"
];

const SOURCE_FIXED_LOCK_COLUMN_IDS = [
    "ZSR_KAIGAIKBN",
    "ZSR_CUR",
    "ZSR_TSUKATANI",
    "ZSR_SHANAIRATE",
    "ZSR_INVOICENO",
    "ZSR_INVOICEEDA"
];

const detailPanelTypesByMaterialCategory = buildRawPurchaseEntryDetailPanelTypes("510", "520", "530");
const baseEntryHooks = buildRawPurchaseEntryReferenceHooks(
    HEADER_LOCK_COLUMN_IDS,
    "参照元の明細は編集できません"
);

function applyRawSalesInEntryRules(context) {
    const reason = String(DicVal(context, "reason", "") ?? "").trim();
    const requestState = isEmpty(context.requestState) ? {} : context.requestState;
    const response = isEmpty(context.response) ? {} : context.response;
    const returnData = DicVal(response, "returnData", {});
    const startupMode = String(DicVal(returnData, "startupMode", "") ?? "").trim();
    const referenceSlipNo = String(DicVal(requestState, "referenceSlipNo", "") ?? "").trim();
    const carryoverMode = String(DicVal(requestState, "carryoverMode", "") ?? "").trim();
    const currentEntryData = isEmpty(context.entrydataRef.current) ? {} : context.entrydataRef.current;
    const nextEntryData = DeepCopy(currentEntryData);
    let headerValueChanged = false;

    if (reason === "after-load-new-entry"
        && startupMode !== "reference-existing"
        && referenceSlipNo === ""
        && carryoverMode === "merge") {
        // 画面設計書では、売上参照画面への遷移直後は関係会社売買として扱います。
        // 既存伝票読込時まで固定値で上書きすると、D_ZSR から復元した値を壊すため、
        // 参照元なし画面からの遷移時だけ国内・JPY・通貨区分/レート空欄・INV空欄を固定表示します。
        if (String(DicVal(nextEntryData, "ZSR_KAIGAIKBN", "") ?? "").trim() !== "1") {
            nextEntryData["ZSR_KAIGAIKBN"] = "1";
            headerValueChanged = true;
        }
        if (String(DicVal(nextEntryData, "ZSR_CUR", "") ?? "").trim() !== "") {
            nextEntryData["ZSR_CUR"] = "";
            headerValueChanged = true;
        }
        if (String(DicVal(nextEntryData, "ZSR_TSUKATANI", "") ?? "").trim() !== "JPY") {
            nextEntryData["ZSR_TSUKATANI"] = "JPY";
            headerValueChanged = true;
        }
        if (String(DicVal(nextEntryData, "ZSR_SHANAIRATE", "") ?? "").trim() !== "") {
            nextEntryData["ZSR_SHANAIRATE"] = "";
            headerValueChanged = true;
        }
        if (String(DicVal(nextEntryData, "ZSR_INVOICENO", "") ?? "").trim() !== "") {
            nextEntryData["ZSR_INVOICENO"] = "";
            headerValueChanged = true;
        }
        if (String(DicVal(nextEntryData, "ZSR_INVOICEEDA", "") ?? "").trim() !== "") {
            nextEntryData["ZSR_INVOICEEDA"] = "";
            headerValueChanged = true;
        }
        if (headerValueChanged) {
            context.entrydataRef.current = nextEntryData;
        }

        const currentFields = Array.isArray(context.textfieldsall) ? context.textfieldsall : [];
        const sourceFields = Array.isArray(context.originalTextfieldsall) && context.originalTextfieldsall.length !== 0
            ? context.originalTextfieldsall
            : currentFields;
        if (Array.isArray(sourceFields) && sourceFields.length !== 0 && currentFields.length !== 0) {
            const nextFields = buildUpdatedRawPurchaseEntryFields(
                currentFields,
                sourceFields,
                SOURCE_FIXED_LOCK_COLUMN_IDS,
                (field, originalField) => {
                    const currentLocked = DicVal(field, "locked", false) === true;
                    if (currentLocked) return field;
                    return {
                        ...field,
                        locked: true
                    };
                }
            );
            if (nextFields !== null) {
                context.ReplaceBaseFields(nextFields);
            }
        }
    }

    applyRawPurchaseEntryReferenceHeaderLocks(context, HEADER_LOCK_COLUMN_IDS);
    if (headerValueChanged) {
        context.TriggerEntryInputRender();
    }
}

const entryHooks = {
    ...baseEntryHooks,
    applyScreenRules: applyRawSalesInEntryRules
};

const entryConfig = {
    screenId: "RawPurchaseEntryRawSalesIn",
    apiController: "RawPurchase",
    dataController: "RawPurchase",
    layoutController: "RawPurchase",
    formKey: "RawPurchaseEntryRawSalesIn",
    listController: "RawSalesIn",
    callerIdentifier: "RawSalesIn",
    sourceCategoryCode: "5",
    primarykeys: ["ZSR_DENNO"],
    sourceCategoryColumnId: RAW_PURCHASE_ENTRY_FIELD_IDS.sourceCategoryCode,
    materialCategoryColumnId: "ZSR_ZAIRYOKBN",
    materialCategoryStateKeys: [
        "materialCategoryCode"
    ],
    detailPanelTypesByMaterialCategory: detailPanelTypesByMaterialCategory,
    hiddenPanelTypesByMaterialCategory: buildRawPurchaseEntryHiddenPanelTypes("510", "520", "530"),
    totalPanelDefinitions: buildRawPurchaseEntryTotalPanelDefinitions("511", "521", "531"),
    entryHooks: entryHooks
};

export const RawPurchaseEntryRawSalesIn = React.forwardRef((props, ref) => {
    return <RawPurchaseEntryBase {...props} ref={ref} entryConfig={entryConfig} />;
});
